/**
 * Hooks for the AI intake endpoints (M4.2 + M4.3).
 *
 * `extract-pdfs` uses a raw fetch because the apiCall wrapper is JSON-only;
 * multipart bypasses Content-Type negotiation by letting the browser write
 * the boundary itself (same pattern as photo upload at api/attachments.ts).
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DesignPackageExtract,
  ExtractionCorrectionsInput,
  ExtractionCorrectionsResult,
  IntakeProposedTask,
  PackingSlipExtract,
  ProjectCreateInput,
  SalesOrderExtract,
} from '@vari-crew/schemas';
import { ApiError, apiCall, getAuthHeaders } from '@/lib/api';
import { env } from '@/lib/env';

export interface ExtractedFile {
  filename: string;
  kind: 'sales_order' | 'packing_slip' | 'design_package' | 'unknown';
  byte_size: number;
  source: 'stub' | 'gemini' | 'failed';
  sales_order?: SalesOrderExtract;
  packing_slip?: PackingSlipExtract;
  design_package?: DesignPackageExtract;
  error?: string;
}

export interface ExtractResponse {
  source: 'stub' | 'gemini' | 'mixed';
  files: ExtractedFile[];
  draft: Partial<ProjectCreateInput> | null;
  proposed_tasks: IntakeProposedTask[];
  warnings: string[];
}

/** A file inserted into a chat turn (image or PDF). */
export interface ChatTurnFile {
  name: string;
  mime_type: string;
  /** Raw base64 (no data: URL prefix) for a small file sent inline. */
  data?: string;
  /** Markdown digest from extractDoc, used instead of `data` for large files. */
  extracted_text?: string;
}

export interface ExtractDocResponse {
  name: string;
  mime_type: string;
  byte_size: number;
  markdown: string;
  source: 'stub' | 'gemini';
}

/**
 * Read one large attachment into Markdown server-side (multipart, same auth as
 * apiCall). Used for files past the inline threshold so the chat carries a
 * compact digest instead of re-sending the bytes inline on every turn.
 */
export async function extractDoc(file: File): Promise<ExtractDocResponse> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${env.API_BASE_URL}/intake/extract-doc`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: fd,
  });
  const ct = res.headers.get('content-type') ?? '';
  const isJson = ct.includes('application/json');
  const payload = isJson ? await res.json() : await res.text();
  if (!res.ok) {
    throw new ApiError({
      status: res.status,
      code: payload?.error?.code ?? 'EXTRACT_FAILED',
      message: payload?.error?.message ?? `HTTP ${res.status}`,
    });
  }
  return payload as ExtractDocResponse;
}

/** A web citation when the assistant grounded a reply with Google Search. */
export interface GroundingSource {
  title: string;
  uri: string;
}

export interface ChatTurn {
  role: 'user' | 'model';
  text: string;
  /** Multimodal attachments the user inserted on this turn. */
  files?: ChatTurnFile[];
  /** Display-only web citations on a model turn (not sent back to the API). */
  sources?: GroundingSource[];
}

export interface ChatResponse {
  reply: string;
  source: 'stub' | 'gemini';
  sources?: GroundingSource[];
}

export interface ChatExtractResponse {
  source: 'stub' | 'gemini';
  draft: Partial<ProjectCreateInput> | null;
}

export function useIntakeMode() {
  return useQuery<{ stub: boolean }>({
    queryKey: ['intake', 'mode'],
    queryFn: () => apiCall<{ stub: boolean }>('/intake/mode'),
    staleTime: 60_000,
  });
}

export function useExtractPdfs() {
  return useMutation<ExtractResponse, Error, File[]>({
    mutationFn: async (files: File[]) => {
      const fd = new FormData();
      for (const f of files) fd.append('file', f);
      const res = await fetch(`${env.API_BASE_URL}/intake/extract-pdfs`, {
        method: 'POST',
        // Same auth as apiCall (real MSAL bearer or mock X-Mock-User); don't
        // set Content-Type so the browser writes the multipart boundary.
        headers: await getAuthHeaders(),
        body: fd,
      });
      const ct = res.headers.get('content-type') ?? '';
      const isJson = ct.includes('application/json');
      const payload = isJson ? await res.json() : await res.text();
      if (!res.ok) {
        throw new ApiError({
          status: res.status,
          code: payload?.error?.code ?? 'EXTRACT_FAILED',
          message: payload?.error?.message ?? `HTTP ${res.status}`,
        });
      }
      return payload as ExtractResponse;
    },
  });
}

export function useIntakeChat() {
  return useMutation<ChatResponse, Error, { history: ChatTurn[] }>({
    mutationFn: ({ history }) =>
      apiCall<ChatResponse>('/intake/chat', {
        method: 'POST',
        body: {
          history,
          // The assistant localizes "now" + relative dates to the user.
          client_tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      }),
  });
}

export function useChatExtract() {
  return useMutation<ChatExtractResponse, Error, { history: ChatTurn[] }>({
    mutationFn: ({ history }) =>
      apiCall<ChatExtractResponse>('/intake/chat/extract', {
        method: 'POST',
        body: { history },
      }),
  });
}

/**
 * Record the PM's edits to AI-extracted fields as eval pairs (EX.9 / D.6).
 * Imperative + best-effort — fired after a project is created, never blocking
 * the create flow. Callers should `.catch()` to swallow failures.
 */
export function recordExtractionCorrections(
  input: ExtractionCorrectionsInput,
): Promise<ExtractionCorrectionsResult> {
  return apiCall<ExtractionCorrectionsResult>('/intake/corrections', {
    method: 'POST',
    body: input,
  });
}
